import type { ObjVal } from '@repo/internal-tools-type-utils/src/utils.ts'
import { cva } from 'class-variance-authority'
import type { StyleGetterProps } from '../../theme/styleGetter.ts'
import {
  COMPONENT_CONTEXTS,
  type TShirtSizeEnumKey,
  type TShirtSizeEnumValue,
} from '../variants.ts'

export const DEFAULT_ICON_CONTEXT = COMPONENT_CONTEXTS.CONTAINED

export const ICON_SIZES = {
  '2XL': '2xl',
  XL: 'xl',
  LG: 'lg',
  MD: 'md',
  SM: 'sm',
  XS: 'xs',
  '3XS': '3xs',
} satisfies Record<TShirtSizeEnumKey, TShirtSizeEnumValue>

export type IconSizes = typeof ICON_SIZES
export type IconSize = ObjVal<IconSizes>

export const DEFAULT_ICON_SIZE = ICON_SIZES.MD

export const getIconContainerLayoutStyle = cva(
  'inline-flex items-center justify-center rounded-icons shrink-0',
  {
    variants: {
      context: {
        [COMPONENT_CONTEXTS.CONTAINED]: undefined,
        [COMPONENT_CONTEXTS.STANDALONE]: undefined,
      },
      size: {
        [ICON_SIZES['2XL']]: undefined,
        [ICON_SIZES.XL]: undefined,
        [ICON_SIZES.LG]: undefined,
        [ICON_SIZES.MD]: undefined,
        [ICON_SIZES.SM]: undefined,
        [ICON_SIZES.XS]: undefined,
        [ICON_SIZES['3XS']]: undefined,
      },
      // This makes the size matches Size Wrapper in figma
      withInnerPadding: {
        true: undefined,
        false: undefined,
      },
    },
    compoundVariants: [
      {
        context: COMPONENT_CONTEXTS.CONTAINED,
        size: ICON_SIZES['2XL'],
        className: 'size-sizing-icons-contained-2xl',
      },
      {
        context: COMPONENT_CONTEXTS.CONTAINED,
        size: ICON_SIZES.XL,
        className: 'size-sizing-icons-contained-xl',
      },
      {
        context: COMPONENT_CONTEXTS.CONTAINED,
        size: ICON_SIZES.LG,
        className: 'size-sizing-icons-contained-lg',
      },
      {
        context: COMPONENT_CONTEXTS.CONTAINED,
        size: ICON_SIZES.MD,
        className: 'size-sizing-icons-contained-md',
      },
      {
        context: COMPONENT_CONTEXTS.CONTAINED,
        size: ICON_SIZES.SM,
        className: 'size-sizing-icons-contained-sm',
      },
      {
        context: COMPONENT_CONTEXTS.CONTAINED,
        size: ICON_SIZES.XS,
        className: 'size-sizing-icons-contained-xs',
      },
      {
        context: COMPONENT_CONTEXTS.CONTAINED,
        size: ICON_SIZES['3XS'],
        className: 'size-2',
      },
      {
        context: COMPONENT_CONTEXTS.STANDALONE,
        size: ICON_SIZES['2XL'],
        className: 'size-sizing-icons-standalone-2xl',
      },
      {
        context: COMPONENT_CONTEXTS.STANDALONE,
        size: ICON_SIZES.XL,
        className: 'size-sizing-icons-standalone-xl',
      },
      {
        context: COMPONENT_CONTEXTS.STANDALONE,
        size: ICON_SIZES.LG,
        className: 'size-sizing-icons-standalone-lg',
      },
      {
        context: COMPONENT_CONTEXTS.STANDALONE,
        size: ICON_SIZES.MD,
        className: 'size-sizing-icons-standalone-md',
      },
      {
        context: COMPONENT_CONTEXTS.STANDALONE,
        size: ICON_SIZES.SM,
        className: 'size-sizing-icons-standalone-sm',
      },
      {
        context: COMPONENT_CONTEXTS.STANDALONE,
        size: ICON_SIZES.XS,
        className: 'size-sizing-icons-standalone-xs',
      },
      {
        context: COMPONENT_CONTEXTS.STANDALONE,
        size: ICON_SIZES['3XS'],
        className: 'size-2.5',
      },

      {
        context: COMPONENT_CONTEXTS.CONTAINED,
        size: ICON_SIZES['2XL'],
        withInnerPadding: true,
        className:
          'size-sizing-contained-2xl [&>svg]:size-sizing-icons-contained-2xl',
      },
      {
        context: COMPONENT_CONTEXTS.CONTAINED,
        size: ICON_SIZES.XL,
        withInnerPadding: true,
        className:
          'size-sizing-contained-xl [&>svg]:size-sizing-icons-contained-xl',
      },
      {
        context: COMPONENT_CONTEXTS.CONTAINED,
        size: ICON_SIZES.LG,
        withInnerPadding: true,
        className:
          'size-sizing-contained-lg [&>svg]:size-sizing-icons-contained-lg',
      },
      {
        context: COMPONENT_CONTEXTS.CONTAINED,
        size: ICON_SIZES.MD,
        withInnerPadding: true,
        className:
          'size-sizing-contained-md [&>svg]:size-sizing-icons-contained-md',
      },
      {
        context: COMPONENT_CONTEXTS.CONTAINED,
        size: ICON_SIZES.SM,
        withInnerPadding: true,
        className:
          'size-sizing-contained-sm [&>svg]:size-sizing-icons-contained-sm',
      },
      {
        context: COMPONENT_CONTEXTS.CONTAINED,
        size: ICON_SIZES.XS,
        withInnerPadding: true,
        className:
          'size-sizing-contained-xs [&>svg]:size-sizing-icons-contained-xs',
      },
      {
        context: COMPONENT_CONTEXTS.CONTAINED,
        size: ICON_SIZES['3XS'],
        withInnerPadding: true,
        className: 'size-2.5 [&>svg]:size-2',
      },
      {
        context: COMPONENT_CONTEXTS.STANDALONE,
        size: ICON_SIZES['2XL'],
        withInnerPadding: true,
        className:
          'size-sizing-standalone-2xl [&>svg]:size-sizing-icons-standalone-2xl',
      },
      {
        context: COMPONENT_CONTEXTS.STANDALONE,
        size: ICON_SIZES.XL,
        withInnerPadding: true,
        className:
          'size-sizing-standalone-xl [&>svg]:size-sizing-icons-standalone-xl',
      },
      {
        context: COMPONENT_CONTEXTS.STANDALONE,
        size: ICON_SIZES.LG,
        withInnerPadding: true,
        className:
          'size-sizing-standalone-lg [&>svg]:size-sizing-icons-standalone-lg',
      },
      {
        context: COMPONENT_CONTEXTS.STANDALONE,
        size: ICON_SIZES.MD,
        withInnerPadding: true,
        className:
          'size-sizing-standalone-md [&>svg]:size-sizing-icons-standalone-md',
      },
      {
        context: COMPONENT_CONTEXTS.STANDALONE,
        size: ICON_SIZES.SM,
        withInnerPadding: true,
        className:
          'size-sizing-standalone-sm [&>svg]:size-sizing-icons-standalone-sm',
      },
      {
        context: COMPONENT_CONTEXTS.STANDALONE,
        size: ICON_SIZES.XS,
        withInnerPadding: true,
        className:
          'size-sizing-standalone-xs [&>svg]:size-sizing-icons-standalone-xs',
      },
      {
        context: COMPONENT_CONTEXTS.STANDALONE,
        size: ICON_SIZES['3XS'],
        withInnerPadding: true,
        className: 'size-4 [&>svg]:size-3',
      },
    ],
    defaultVariants: {
      context: DEFAULT_ICON_CONTEXT,
      size: DEFAULT_ICON_SIZE,
      withInnerPadding: false,
    },
  },
)

type GetIconContainerLayoutStyle = typeof getIconContainerLayoutStyle
export type GetIconContainerLayoutStyleProps =
  StyleGetterProps<GetIconContainerLayoutStyle>
